import { useQuery, keepPreviousData } from "@tanstack/react-query";
import type { Environment } from "../../../types";
import type {
  TokenPriceHistoryResponse,
  TokenPriceInterval,
  TokenPricePoint,
} from "../types";
import { getApiBase } from "../../../lib/remote-config";

export const TOKEN_PRICE_HISTORY_QUERY_KEY = "token-price-history";

/**
 * 특정 토큰의 가격 히스토리를 `/v1/public/token/price/historical`에서 조회.
 * dapp-ui가 이미 사용하는 walletServer 호스트를 그대로 쓴다. interval 전환 시
 * `keepPreviousData`로 이전 데이터를 유지해 차트 깜빡임을 막는다.
 */
export function useTokenPriceHistory(
  env: Environment,
  chainId: number,
  token: string,
  interval: TokenPriceInterval,
  enabled = true,
) {
  return useQuery<TokenPricePoint[]>({
    queryKey: [
      TOKEN_PRICE_HISTORY_QUERY_KEY,
      env,
      chainId,
      token.toLowerCase(),
      interval,
    ],
    queryFn: async ({ signal }) => {
      const baseUrl = await getApiBase("walletServer", env);
      const params = new URLSearchParams({
        chain_id: String(chainId),
        token,
        interval,
      });
      const res = await fetch(
        `${baseUrl}/v1/public/token/price/historical?${params}`,
        { cache: "no-store", signal },
      );
      if (!res.ok) {
        throw new Error(`Failed to fetch token price history: ${res.status}`);
      }
      const json: TokenPriceHistoryResponse = await res.json();
      if (json.code !== 200) {
        throw new Error(`Token price history API error: ${json.code}`);
      }
      return json.data ?? [];
    },
    enabled: enabled && !!token && chainId > 0,
    staleTime: 30_000,
    placeholderData: keepPreviousData,
  });
}
